import { getAspectRatioForImages } from '@/utils/aspectRatioUtils';

import { MediaWrapper } from '@/components/Cms/MediaWrapper';
import type { ContentfulImageWithFocalPointFragment } from '@/graphql/contentful/api';
import { useDeviceDetect } from '@/hooks/useDeviceDetect';
import type { CardContentMaxWidth } from '@/lib/types';

import type { CardProps } from '../../types';
import { CardContent } from '../CardContent';
import { getDeskTopMaxContentWidth } from './helpers';

import * as S from './BaseCardType.styles';

export const BaseCardType = (props: CardProps) => {
  const {
    aspectRatio,
    backgroundAlignment,
    backgroundColor,
    className,
    contentAlignment,
    contentMaxWidth,
    isHeroCard,
    isSectionBannerCard = false,
    media,
    mediaAnimationEffect,
    mediaWrapperType,
    preload,
    textAlignment,
    type,
    visibleInDesktop,
    visibleInMobile,
    withBackgroundImage = true,
    withWrapperMinHeight = true,
  } = props;
  const { deviceType } = useDeviceDetect();
  const hasBackgroundImage = !!media?.image?.url;
  const hasWrapperMinHeight = withWrapperMinHeight;
  const deskTopContentMaxWidth = getDeskTopMaxContentWidth(
    type as string,
    contentMaxWidth as CardContentMaxWidth,
  );
  const aspectValue = aspectRatio ??
    getAspectRatioForImages(mediaWrapperType, null, null, null, deviceType) ?? {
      x: 0,
      y: 0,
    };
  return (
    <S.Wrapper
      className={className}
      contentAlignment={contentAlignment}
      data-test-id='BaseCardTypeWrapper'
      hasBackgroundImage={hasBackgroundImage}
      hasWrapperMinHeight={hasWrapperMinHeight}
      heroCard={!!isHeroCard}
      mediaAnimationEffect={
        mediaAnimationEffect === 'zoomOnHover' ? mediaAnimationEffect : null
      }
      sectionBannerCard={isSectionBannerCard}
      type={type}
      visibleInDesktop={visibleInDesktop}
      visibleInMobile={visibleInMobile}
    >
      {backgroundColor?.value && (
        <S.Background
          $backgroundColor={backgroundColor?.value}
          data-test-id='BaseCardTypeBackground'
        />
      )}
      {withBackgroundImage && media?.image?.url && (
        <S.Media $backgroundAlignment={backgroundAlignment}>
          <MediaWrapper
            {...(media as ContentfulImageWithFocalPointFragment)}
            aspectRatio={aspectValue}
            data-test-id='BaseCardTypeBackgroundImage'
            mediaWrapperType={mediaWrapperType}
            preload={preload}
            sizes='(max-width: 600px) 50vw, 20vw'
          />
        </S.Media>
      )}
      <S.Content
        className={`${className}Content`}
        data-test-id='BaseCardTypeContent'
        deskTopContentMaxWidth={deskTopContentMaxWidth}
        hasWrapperMinHeight={hasWrapperMinHeight}
        sectionBannerCard={isSectionBannerCard}
        textAlignment={textAlignment}
        type={type}
      >
        <CardContent {...props} />
      </S.Content>
    </S.Wrapper>
  );
};
